import { WifiOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { cn } from '@repo/ui/lib/utils'

export interface OfflineIndicatorProps {
  /** Whether the user is currently online */
  isOnline: boolean
  /** Duration in milliseconds to show the indicator after coming back online */
  hideDelay?: number
  /** Custom class name for the container */
  className?: string
  /** Custom icon component */
  icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
  /** Custom messages for online/offline states */
  messages?: {
    online: string
    offline: string
  }
  /** Whether to show the indicator when offline (default: true) */
  showWhenOffline?: boolean
  /** Whether to show the indicator when coming back online (default: true) */
  showWhenOnline?: boolean
}

export function OfflineIndicator({
  isOnline,
  hideDelay = 1000,
  className,
  icon: Icon = WifiOff,
  messages = {
    online: 'You are back online.',
    offline: 'You are currently offline. Changes will sync when connection is restored.',
  },
  showWhenOffline = true,
  showWhenOnline = false,
}: OfflineIndicatorProps) {
  const [isVisible, setIsVisible] = useState(() => !isOnline && showWhenOffline)

  useEffect(() => {
    if (!isOnline && showWhenOffline) {
      setIsVisible(true)
    } else if (isOnline && showWhenOnline) {
      setIsVisible(true)
      const timeout = window.setTimeout(() => setIsVisible(false), hideDelay)
      return () => window.clearTimeout(timeout)
    } else if (isOnline && !showWhenOnline) {
      setIsVisible(false)
    }
    return undefined
  }, [isOnline, hideDelay, showWhenOffline, showWhenOnline])

  if (!isVisible) {
    return null
  }

  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-md border border-dashed border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground',
        className
      )}
    >
      <Icon className="h-4 w-4" aria-hidden={true} />
      <span>
        {isOnline ? messages.online : messages.offline}
      </span>
    </div>
  )
}
